import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi, adminStore } from '../../adminApi';
import { IconArrowLeft, IconArrowRight } from '../../components/Icon';
import { LOGOS } from '../../cdn';

export default function AdminLogin() {
  const nav = useNavigate();
  const [u, setU] = useState('');
  const [p, setP] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'login'|'mfa'>('login');
  const [qr, setQr] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function doLogin() {
    setErr(null); setBusy(true);
    const { status, data } = await adminApi.login(u.trim(), p);
    setBusy(false);
    if (status !== 200) { setErr(data?.message || 'Login failed'); return; }
    if (data?.need_2fa) {
      if (data.setup) { setSecret(data.secret); setQr(data.qr_url); }
      setStep('mfa');
    } else if (data?.token) {
      adminStore.setToken(data.token);
      nav('/admin', { replace: true });
    }
  }

  async function doVerify() {
    setErr(null); setBusy(true);
    const { status, data } = await adminApi.verify2fa(u.trim(), code.trim());
    setBusy(false);
    if (status !== 200) { setErr(data?.message || 'Invalid code'); return; }
    nav('/admin', { replace: true });
  }

  return (
    <div className="min-h-screen grid place-items-center bg-[color:var(--color-bg)] px-4 py-12 relative overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[color:var(--color-brand)]/12 blur-[100px]" />
      <div aria-hidden className="pointer-events-none absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[color:var(--color-brand-deep)]/12 blur-[100px]" />
      <div className="w-full max-w-md rounded-[24px] border border-[color:var(--color-brand)]/20 bg-[color:var(--color-surface)] p-8 shadow-2xl relative">
        <div className="text-center">
          <div className="mx-auto w-14 h-14 rounded-2xl overflow-hidden ring-1 ring-[color:var(--color-brand)]/20 bg-[color:var(--color-bg)] grid place-items-center">
            <img src={LOGOS.vpnee} alt="Vpnee" width={40} height={40} className="w-full h-full object-cover" />
          </div>
          <h1 className="h-title text-3xl font-extrabold mt-3">Vpnee Admin</h1>
          <p className="text-sm text-[color:var(--color-ink-dim)] mt-1">MFA protected · HMAC secured</p>
        </div>

        {step === 'login' ? (
          <>
            <label className="block mt-6 text-xs uppercase tracking-widest text-[color:var(--color-ink-faint)]">Username</label>
            <input value={u} onChange={e=>setU(e.target.value)} className="mt-1 w-full rounded-xl bg-black/30 border border-[color:var(--color-line)] px-4 py-3 text-sm outline-none focus:border-[color:var(--color-brand)]" placeholder="Username" autoFocus />
            <label className="block mt-4 text-xs uppercase tracking-widest text-[color:var(--color-ink-faint)]">Password</label>
            <input type="password" value={p} onChange={e=>setP(e.target.value)} onKeyDown={e=>e.key==='Enter'&&doLogin()} className="mt-1 w-full rounded-xl bg-black/30 border border-[color:var(--color-line)] px-4 py-3 text-sm outline-none focus:border-[color:var(--color-brand)]" placeholder="••••••••" />
            {err && <div className="mt-3 text-sm text-red-400">{err}</div>}
            <button disabled={busy} onClick={doLogin} className="btn btn-primary w-full mt-6 justify-center inline-flex items-center gap-2">{busy?'Signing in…':<>Continue <IconArrowRight width={14} height={14} /></>}</button>
          </>
        ) : (
          <>
            <div className="mt-6 rounded-xl border border-[color:var(--color-line)] bg-black/20 p-4">
              <div className="text-sm font-bold">Two-factor authentication</div>
              <div className="text-xs text-[color:var(--color-ink-dim)] mt-1">Open Google Authenticator / Authy and enter the 6-digit code.</div>
              {qr && (
                <div className="mt-4 text-center">
                  <img src={qr} alt="QR" className="mx-auto w-40 h-40 rounded-xl border border-white/10" />
                  <div className="mt-2 font-mono text-xs break-all text-[color:var(--color-brand-glow)]">{secret}</div>
                  <div className="text-[10px] text-[color:var(--color-ink-faint)]">Scan this once, then it disappears</div>
                </div>
              )}
              <input value={code} onChange={e=>setCode(e.target.value.replace(/\D/g,''))} maxLength={6} onKeyDown={e=>e.key==='Enter'&&doVerify()} placeholder="123456" className="mt-4 w-full rounded-xl bg-black/40 border border-[color:var(--color-line)] px-6 py-4 text-center tracking-[0.4em] font-mono text-lg outline-none focus:border-[color:var(--color-brand)]" />
              {err && <div className="mt-3 text-sm text-red-400 text-center">{err}</div>}
              <div className="flex gap-4 mt-6">
                <button onClick={()=>setStep('login')} className="btn btn-ghost flex-1 justify-center">Back</button>
                <button disabled={busy || code.length!==6} onClick={doVerify} className="btn btn-primary flex-1 justify-center disabled:opacity-50">{busy?'Verifying…':'Verify'}</button>
              </div>
            </div>
          </>
        )}

        <div className="text-center mt-6 text-xs text-[color:var(--color-ink-faint)]"><a href="/" className="inline-flex items-center gap-1 hover:text-white"><IconArrowLeft width={12} height={12} /> Back to site</a></div>
      </div>
    </div>
  );
}
